/**
 * API client — thin fetch wrapper with typed responses.
 * All calls go through here so auth/error handling is centralized.
 */
import type { ApiError, Paginated } from '@shared/types';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: ApiError;
  constructor(status: number, body: ApiError) {
    super(body.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.body = body;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  opts?: { json?: boolean },
  hasRetried = false,
): Promise<T> {
  const asJson = opts?.json !== false;
  const headers = new Headers(options.headers);
  // JSON bodies need Content-Type; FormData must NOT set it (browser adds boundary).
  if (asJson && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, {
    ...options,
    credentials: 'include',
    headers,
  });

  const refreshable =
    res.status === 401 &&
    !hasRetried &&
    path !== '/api/auth/signin' &&
    path !== '/api/auth/signout' &&
    path !== '/api/auth/refresh';

  if (refreshable && await refreshAccessToken()) {
    return request<T>(path, options, opts, true);
  }

  if (!res.ok) {
    let body: ApiError;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      body = { code: 'INTERNAL', message: `HTTP ${res.status}` };
    }
    throw new ApiRequestError(res.status, body);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(
      path,
      {
        method: 'POST',
        body: formData,
      },
      { json: false },
    ),
};

export type { Paginated };

/**
 * Open protected API content without navigating the browser directly to an
 * `/api/*` URL. This keeps attachment/PDF views working with SPA asset
 * fallbacks that otherwise intercept top-level navigations.
 */
export async function openApiContent(path: string): Promise<void> {
  const popup = window.open('', '_blank');
  if (!popup) throw new Error('Your browser blocked the new tab. Please allow pop-ups and try again.');

  try {
    const response = await fetch(path, { credentials: 'include' });
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        message = ((await response.json()) as ApiError).message || message;
      } catch {
        // The status is sufficient if the error response is not JSON.
      }
      throw new Error(message);
    }

    const objectUrl = URL.createObjectURL(await response.blob());
    popup.location.replace(objectUrl);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    popup.close();
    throw error;
  }
}

/**
 * Build a query string from a params object, omitting undefined/null/empty
 * values. Returns '' when nothing is present so callers can blindly append.
 */
export function qs(
  params?: Record<string, string | number | undefined | null>,
): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of entries) sp.set(k, String(v));
  return `?${sp.toString()}`;
}
